import axios from 'axios';
import TokenManager from '../utils/tokenManager';

export const SESSION_EXPIRED_EVENT = 'auth:session-expired';
export const TOKEN_REFRESH_FAILED_EVENT = 'auth:token-refresh-failed';

// Separate from the legacy client, which reads localStorage.token.
const client = axios.create({
  baseURL: '/',
  timeout: 100000,
  headers: { 'Content-Type': 'application/json' },
});

let refreshPromise = null;

function authError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function expireSession() {
  TokenManager.clearAllTokens();
  localStorage.removeItem('uid');
  localStorage.removeItem('userInfo');
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  return authError('AUTH_LOGIN_REQUIRED', '登录已过期，请重新登录');
}

function refreshFailed() {
  window.dispatchEvent(new Event(TOKEN_REFRESH_FAILED_EVENT));
  return authError('AUTH_RELOGIN_REQUIRED', '近期有黑客盗取客户卡密的行为，为了保护您的权益，如果查询订单请重新登录。');
}

function isAccessTokenExpired(error) {
  const body = error.response?.data;
  return error.response?.status === 401 && (
    body?.code === 'AXTORA_ACCESS_TOKEN_EXPIRED' ||
    body?.code === 'UNAUTHORIZED' ||
    (body?.data?.reason === 'access_token_expired' && body?.data?.can_refresh === true)
  );
}

async function refreshAccessToken() {
  const refreshToken = TokenManager.getRefreshToken();
  const previousAccessToken = TokenManager.getAccessToken();
  if (!refreshToken || !refreshToken.trim()) throw refreshFailed();

  // A response for an old login must not overwrite or clear a newer login.
  const sessionUnchanged = () => (
    TokenManager.getRefreshToken() === refreshToken &&
    TokenManager.getAccessToken() === previousAccessToken
  );

  let response;
  try {
    response = await client.post('/testapi/user/stander_login_controller/refresh_token', {
      refresh_token: refreshToken,
    }, { timeout: 15000 });
  } catch (error) {
    if (!sessionUnchanged()) {
      throw authError('AUTH_SESSION_CHANGED', '登录状态已变化，请重新查询订单');
    }
    throw refreshFailed();
  }

  if (!sessionUnchanged()) {
    throw authError('AUTH_SESSION_CHANGED', '登录状态已变化，请重新查询订单');
  }
  const token = response.data?.tokens?.access_token;
  if (response.data?.code !== 1 || typeof token !== 'string' || !token.trim()) {
    throw refreshFailed();
  }

  TokenManager.setAccessToken(token);
  // The refresh token is intentionally retained, even if the response includes one.
  return token;
}

function getRefreshedAccessToken() {
  if (!refreshPromise) {
    refreshPromise = Promise.resolve().then(refreshAccessToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export async function authenticatedPost(url, data, { onResponse, skipTokenValidation = false } = {}) {
  const accessToken = TokenManager.getAccessToken();
  const refreshToken = TokenManager.getRefreshToken();
  const send = (token) => client.post(url, data, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  let token = accessToken;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    let body;
    let needsRefresh = false;
    try {
      body = (await send(token)).data;
    } catch (error) {
      // 如果不是 Token 过期错误，或者跳过 Token 验证，直接抛出原始错误
      if (!isAccessTokenExpired(error) || skipTokenValidation) throw error;
      // 第二次请求失败（刷新 Token 后仍然失败），抛出自定义错误
      if (attempt === 1) throw refreshFailed();
      needsRefresh = true;
    }

    if (!skipTokenValidation && TokenManager.getRefreshToken() !== refreshToken) {
      throw authError('AUTH_SESSION_CHANGED', '登录状态已变化，请重新查询订单');
    }

    if (!needsRefresh) {
      // Observe-only responses contain usable orders before authentication is repaired.
      onResponse?.(body);
      const auth = body?.data;
      if (!skipTokenValidation &&
          (['NO_TOKEN', 'TOKEN_INVALID', 'USER_KEY_MISMATCH'].includes(auth?.auth_status) ||
          auth?.access_token_invalid === true)) {
        throw expireSession();
      }
      needsRefresh = !skipTokenValidation && auth?.can_refresh === true;
      if (!needsRefresh) return body;
      if (attempt === 1) {
        throw authError('AUTH_REFRESH_FAILED', '登录凭证刷新后仍已过期，请稍后重试');
      }
    }

    // A concurrent request may already have finished refreshing this token.
    const currentToken = TokenManager.getAccessToken();
    token = currentToken && currentToken !== accessToken
      ? currentToken
      : await getRefreshedAccessToken();
  }
}
